import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import PageHeader from '../components/PageHeader'
import DonateModal from '../components/DonateModal'
import { 
  AcademicCapIcon, 
  BookOpenIcon, 
  EyeIcon, 
  HeartIcon, 
  UserGroupIcon, 
  BriefcaseIcon, 
  BuildingOfficeIcon, 
  ArrowLongRightIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline'

const journeyTimeline = [
  {
    period: "2013-2016",
    title: "The Journey Begins: School Feeding and Deworming Initiative",
    description: "PACIEH launched a community-driven school health, education, and socioeconomic development project in 2013, focusing on providing school meals to improve student attendance and learning outcomes. School kitchens were constructed and only mothers who had their children in the schools took turns to cook for the students. This initiative led to a significant increase in school enrollment and retention rates, with students aged 2–14 years benefiting from the program.",
    icon: AcademicCapIcon,
  },
  {
    period: "2016",
    title: "Girl-Child Education Research",
    description: "A research study titled 'Girl Child Education in Anambra State: challenges and way forward' was conducted to address the barriers to education faced by girls. This important research provided valuable recommendations to improve girl-child education in the region, addressing key challenges and creating pathways for better educational outcomes.",
    icon: BookOpenIcon,
  },
  {
    period: "2017",
    title: "Monitoring Learning Achievements",
    description: "PACIEH coordinated four multidisciplinary research studies funded by the Anambra State Government and The World Bank, aiming to enhance the educational performances of secondary school students. This initiative resulted in improved performances of Anambra State secondary school students in national and international competitions over the last four years.",
    icon: BriefcaseIcon,
  },
  {
    period: "2018",
    title: "Monitoring Learning Achievements of Science Teachers",
    description: "PACIEH, funded by the Anambra State Government and The World Bank, developed standardized tests to assess student learning in public schools. The initiative led to improved policies and better academic performance.",
    icon: UserGroupIcon,
  },
  {
    period: "2016-2020",
    title: "School Eye Health Programs",
    description: "PACIEH advocated for and implemented eye screening programs in remote communities, with sponsorship from trustees like Barrister Onubogu. Hundreds of pupils were screened for ocular pathologies, leading to early detection and treatment, significantly improving their quality of life and educational outcomes.",
    icon: EyeIcon,
  },
  {
    period: "2023",
    title: "Goodwill Medical Centre",
    description: "Commissioned on November 23, 2023, and opened to the public on February 1, 2024, the Goodwill Medical Centre aims to serve poor, low-income families, and marginalized communities. The center has undertaken high-impact initiatives designed to improve access to healthcare services, continuing PACIEH's mission of transforming lives through healthcare.",
    icon: HeartIcon,
  },
  {
    period: "2024",
    title: "Training of Traditional Birth Attendants",
    description: "Goodwill Medical Centre, in collaboration with the Enugu State Primary Healthcare Agency, initiated a geospatial mapping project to identify and engage Traditional Birth Attendants (TBAs) in nearby communities. This initiative aimed to address the reliance on TBAs due to healthcare access barriers, which had led to preventable maternal and infant deaths. This program significantly improved maternal and neonatal health outcomes in the region.",
    icon: UserGroupIcon,
  },
  {
    period: "2024",
    title: "The Bridge Project",
    description: "The Bridge Project connects the Iffo Community to Goodwill Medical Centre, ensuring safe and easy access to healthcare for low-income and underprivileged families. Impact: 1. Safe access to medical care 2. Stronger community connections 3. Improved quality of life. This bridge is more than just a structure—it's a lifeline to better health.",
    icon: BuildingOfficeIcon,
  },
  {
    period: "2026",
    title: "Global NTD Alliance & 17th Annual NNN Kigali Conference",
    description: "PACIEH CEO Prof. Chinyelu Angela Ekwunife represented the organization at the 17th Annual NTD NGO Network Conference at Kigali Marriott Hotel, Rwanda. Forged bilateral alliances with MITOSATH, DAHW Germany, and University of Kigali to integrate Neglected Tropical Diseases (NTD) elimination with community school health, feeding programs, and maternal care.",
    icon: GlobeAltIcon,
  },
]

const programs = [
  {
    id: 'school-feeding',
    title: 'School Feeding Program',
    year: '2013',
    description: `PACIEH's School Feeding Program is a comprehensive initiative launched in 2013 that combines nutrition, education, and community development. The program provides nutritious meals to students, improving attendance rates and learning outcomes while supporting local farmers and food vendors.`,
    details: [
      'Serves over 10,000 students across multiple schools',
      'Partners with local farmers and food vendors',
      'Includes nutrition education and hygiene practices',
      'Monitors student health and academic performance',
      'Creates employment opportunities for community members'
    ]
  },
  {
    id: 'girl-child-education',
    title: 'Girl-Child Education Study',
    year: '2016',
    description: `Our research study in Anambra State identified key barriers to girl-child education and proposed effective solutions. The study has influenced policy decisions and program implementations to improve educational access and outcomes for girls in the region.`,
    details: [
      'Comprehensive research on educational barriers',
      'Policy recommendations for government agencies',
      'Community engagement and awareness programs',
      'Mentorship initiatives for young girls',
      'Collaboration with local schools and leaders'
    ]
  },
  {
    id: 'eye-health',
    title: 'School Eye Health Initiative',
    year: '2016',
    description: `The Eye Health Program brings vital vision care services to remote communities. Through regular screening programs and partnerships with eye care professionals, we help detect and treat vision problems early, ensuring better educational outcomes.`,
    details: [
      'Regular vision screening in schools',
      'Provision of eyeglasses to students in need',
      'Training for teachers in basic vision screening',
      'Referral system for advanced eye care',
      'Community education on eye health'
    ]
  },
  {
    id: 'learning-achievements',
    title: 'Learning Achievements Monitoring',
    year: '2017',
    description: `In partnership with the Anambra State Government and The World Bank, PACIEH coordinated comprehensive research studies to assess and enhance educational performance in public schools.`,
    details: [
      'Development of assessment tools',
      'Teacher training programs',
      'Data collection and analysis',
      'Policy recommendations',
      'Implementation of improvement strategies'
    ]
  },
  {
    id: 'teacher-development',
    title: 'Science Teachers Development',
    year: '2018',
    description: `Our teacher development program focuses on enhancing the quality of science education through comprehensive training and support for teachers. The program includes standardized testing methods and innovative teaching techniques.`,
    details: [
      'Professional development workshops',
      'Curriculum development support',
      'Laboratory management training',
      'Assessment methodology training',
      'Ongoing mentorship and support'
    ]
  },
  {
    id: 'medical-centre',
    title: 'Goodwill Medical Centre',
    year: '2023',
    description: `The Goodwill Medical Centre serves as a vital healthcare hub for underserved communities. Our facility provides accessible, quality healthcare services to poor and low-income families, ensuring that essential medical care reaches those who need it most.`,
    details: [
      'Primary healthcare services',
      'Maternal and child health care',
      'Preventive health programs',
      'Health education initiatives',
      'Community outreach services'
    ]
  },
  {
    id: 'birth-attendants',
    title: 'Traditional Birth Attendants Training',
    year: '2024',
    description: `In collaboration with Enugu State Primary Healthcare Agency, we provide comprehensive training for Traditional Birth Attendants to improve maternal and neonatal health outcomes in rural communities.`,
    details: [
      'Safe delivery practices training',
      'Emergency response protocols',
      'Maternal health monitoring',
      'Newborn care education',
      'Referral system integration'
    ]
  },
  {
    id: 'bridge-project',
    title: 'The Bridge Project',
    year: '2024',
    description: `The Bridge Project represents our commitment to improving healthcare accessibility. By constructing a bridge connecting Iffo Community to the Goodwill Medical Centre, we're ensuring safe and reliable access to healthcare services for all community members.`,
    details: [
      'Infrastructure development',
      'Community access improvement',
      'Emergency response facilitation',
      'Economic opportunity enhancement',
      'Community engagement and ownership'
    ]
  }
];

function Programs() {
  const [donateModalOpen, setDonateModalOpen] = useState(false)
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      // Get the element with the ID
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        // Wait a bit for animations to complete
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>Our Programs | PACIEH</title>
        <meta name="description" content="Explore PACIEH's initiatives in education, healthcare, and community development across Africa." />
      </Helmet>

      <PageHeader
        title="Our Programs"
        subtitle="From School feeding to Healthcare: A decade of transforming lives through sustainable community initiatives."
      />

      {/* Timeline Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            {journeyTimeline.map((milestone, index) => (
              <motion.div
                key={milestone.period}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="relative pb-12 last:pb-0"
              >
                {/* Timeline line */}
                {index !== journeyTimeline.length - 1 && (
                  <div className="absolute left-8 top-14 bottom-0 w-0.5 bg-pacieh-blue/20" />
                )}

                <div className="relative flex items-start gap-6">
                  {/* Icon */}
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-pacieh-blue text-white shadow-lg">
                    <milestone.icon className="h-8 w-8" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow duration-300">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="px-4 py-1.5 rounded-full bg-pacieh-green/10 text-pacieh-green font-semibold text-sm">
                          {milestone.period}
                        </span>
                        <ArrowLongRightIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-bold text-pacieh-blue mb-3">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-24">
            {programs.map((program, index) => (
              <motion.div
                key={program.id}
                id={program.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="scroll-mt-24"
              >
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                      <span className="inline-flex items-center rounded-full bg-pacieh-green/10 px-3 py-1 text-sm font-medium text-pacieh-green">
                        {program.year}
                      </span>
                      <h2 className="text-2xl font-bold text-pacieh-blue">
                        {program.title}
                      </h2>
                    </div>
                    
                  <p className="text-gray-600 mb-8">
                    {program.description}
                  </p>

                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-pacieh-blue mb-4">
                        Key Components
                      </h3>
                      <ul className="space-y-3">
                        {program.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="flex-shrink-0 h-6 w-6 rounded-full bg-pacieh-green/10 flex items-center justify-center mr-3">
                              <span className="h-2 w-2 rounded-full bg-pacieh-green"></span>
                            </span>
                            <span className="text-gray-600">{detail}</span>
                          </li>
                        ))}
                      </ul>
                      </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-pacieh-blue py-16">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6">
              Be Part of Our Journey
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Join us in creating lasting positive change in African communities through education, research, and healthcare initiatives.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                type="button"
                onClick={() => setDonateModalOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary bg-white text-pacieh-blue hover:bg-gray-100 font-bold shadow-md"
              >
                Donate Now
              </motion.button>
              <motion.a
                href="https://forms.gle/bss8TuKU6U9xXCZ89"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary bg-transparent text-white border-white hover:bg-white/10"
              >
                Join Us
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      {/* Global Donation Modal */}
      <DonateModal
        isOpen={donateModalOpen}
        onClose={() => setDonateModalOpen(false)}
      />
    </>
  )
}

export default Programs 